import { LoadingArea } from '@consigli/facade';
import { useProjectId, usePackageId, useGetBlobsQuery, usePackage } from '@consigli/hooks';
import { Document, DocumentType } from '@consigli/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HiDocumentText } from 'react-icons/hi';

import Specifications from '@/assets/images/layout/doc_img.png';
import Reports from '@/assets/images/layout/reports_img.png';
import { CardBasedFileDisplay } from '@/components/card-based-file-display';
import { DismissibleDisclaimer } from '@/components/dismissible-disclaimer';
import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { useDemoMode } from '@/util/demo-mode';

const REPORT_GENERATION_SECTIONS = [
  {
    key: 'sample-report',
    translationKey: 'report-generation.sample-report',
    descriptionKey: 'report-generation.sample-report-description',
    image: Reports,
    groupingFn: (doc: Document) => {
      const blob = doc as unknown as {
        metadata?: { is_sample_report?: boolean; isSampleReport?: boolean };
      };
      return blob.metadata?.is_sample_report || blob.metadata?.isSampleReport || false;
    },
  },
  {
    key: 'uploaded-files',
    translationKey: 'report-generation.uploaded-files',
    descriptionKey: 'report-generation.uploaded-files-description',
    image: Specifications,
    groupingFn: (doc: Document) => {
      const blob = doc as unknown as {
        metadata?: { is_sample_report?: boolean; isSampleReport?: boolean };
      };
      return !(blob.metadata?.is_sample_report || blob.metadata?.isSampleReport);
    },
  },
];

export const ReportGenerationDocumentsPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { adminFeaturesEnabled } = useDemoMode();

  const { pkg } = usePackage(projectId, packageId);
  const isPublished = pkg ? pkg.published : false;

  const {
    data: blobs,
    isLoading,
    isFetching,
    isError,
  } = useGetBlobsQuery(
    {
      projectId: projectId!,
      packageId: packageId!,
      page: 'all',
    },
    { skip: !projectId || !packageId },
  );

  const allDocuments = useMemo(() => {
    if (!blobs?.results) {
      return [];
    }

    const documents: Document[] = [];

    blobs.results.forEach((blob) => {
      const extension = blob.extension?.toLowerCase().replace(/^\./, '');
      if (extension === 'md') {
        return;
      }

      const document: Document = {
        id: blob.id,
        name: blob.name,
        extension: blob.extension,
        fileUrl: blob.fileUrl,
        downloadUrl: blob.downloadUrl,
        type: DocumentType.BLOB,
        documentPackageId: blob.documentPackage,
        ns3451: blob.ns3451 || [],
        parsedData: blob.parsedData || {},
        convertedPdfFileId: blob.convertedPdfFileId,
        ...(blob.metadata && { metadata: blob.metadata }),
      };

      documents.push(document);
    });

    return documents;
  }, [blobs]);

  const waitingForAnalysis = useMemo(() => {
    if (adminFeaturesEnabled) {
      return false;
    }
    if (isPublished) {
      return false;
    }
    return true;
  }, [isPublished, adminFeaturesEnabled]);

  const hasDocuments = allDocuments.length > 0;

  if (!packageId) {
    return (
      <WorkspaceProvider>
        <WorkspaceLayout hideTabs={true}>
          <Breadcrumb label={t('servicetabs.documents')} />
          <LoadingArea title={t('document-list.loading')} />
        </WorkspaceLayout>
      </WorkspaceProvider>
    );
  }

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={!packageId || waitingForAnalysis}>
        <Breadcrumb label={t('servicetabs.documents')} />
        {waitingForAnalysis ? (
          <WaitingForAnalysis />
        ) : isLoading ? (
          <LoadingArea title={t('document-list.loading')} />
        ) : isError || !hasDocuments ? (
          <div className="flex items-center justify-center w-full min-h-[calc(100vh-200px)]">
            <div className="text-center">
              <HiDocumentText className="mx-auto h-16 w-16 text-day-neutral-subtle mb-4" />
              <h3 className="text-xl font-medium text-day-neutral-subtle mb-2">
                {t('report-generation.no-documents-title')}
              </h3>
              <p className="text-night-light-5 max-w-md">
                {t('report-generation.no-documents-description')}
              </p>
            </div>
          </div>
        ) : (
          <>
            <DismissibleDisclaimer>{t('optimization-tabs.ai-disclaimer')}</DismissibleDisclaimer>

            <CardBasedFileDisplay
              files={allDocuments}
              isFetching={isFetching}
              isPublished={isPublished}
              sections={REPORT_GENERATION_SECTIONS}
              showSearch={false}
              showDownloadBySection={true}
            />
          </>
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
